import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions' | 'users' | 'networks' | 'reports'>('overview');

  // بيانات وهمية للعرض
  const stats = [
    { label: 'إجمالي الإيرادات اليوم', value: '2,450,000 فرنك', icon: 'ri-money-dollar-circle-line', color: 'from-green-500 to-green-600', change: '+12%' },
    { label: 'عدد المستخدمين النشطين', value: '1,234', icon: 'ri-user-line', color: 'from-blue-500 to-blue-600', change: '+8%' },
    { label: 'المعاملات اليوم', value: '456', icon: 'ri-exchange-line', color: 'from-purple-500 to-purple-600', change: '+15%' },
    { label: 'الشبكات التجارية', value: '89', icon: 'ri-store-2-line', color: 'from-orange-500 to-orange-600', change: '+3%' }
  ];

  const recentTransactions = [
    { id: 'TXN001', user: 'أحمد محمد', package: 'الباقة المميزة', amount: 3500, method: 'Orange Money', status: 'مكتمل', time: 'منذ 5 دقائق' },
    { id: 'TXN002', user: 'فاطمة علي', package: 'الباقة القياسية', amount: 2000, method: 'Moov Money', status: 'مكتمل', time: 'منذ 12 دقيقة' },
    { id: 'TXN003', user: 'عمر حسن', package: 'الباقة الشاملة', amount: 15000, method: 'MTN', status: 'قيد المعالجة', time: 'منذ 18 دقيقة' },
    { id: 'TXN004', user: 'سارة أحمد', package: 'الباقة الأساسية', amount: 500, method: 'Wave', status: 'مكتمل', time: 'منذ 25 دقيقة' },
    { id: 'TXN005', user: 'محمد عبدالله', package: 'الباقة المميزة', amount: 3500, method: 'Orange Money', status: 'مكتمل', time: 'منذ 32 دقيقة' }
  ];

  const activeUsers = [
    { username: 'user1234', package: 'الباقة المميزة', timeLeft: '18 ساعة', dataUsed: '2.3 GB', speed: '20 Mbps', location: 'مقهى النور' },
    { username: 'user5678', package: 'الباقة القياسية', timeLeft: '4 ساعات', dataUsed: '850 MB', speed: '10 Mbps', location: 'فندق السلام' },
    { username: 'user9012', package: 'الباقة الشاملة', timeLeft: '5 أيام', dataUsed: '15.7 GB', speed: '50 Mbps', location: 'مركز الأعمال' },
    { username: 'user3456', package: 'الباقة الأساسية', timeLeft: '45 دقيقة', dataUsed: '320 MB', speed: '5 Mbps', location: 'مطعم الواحة' }
  ];

  const commercialNetworks = [
    { name: 'مقهى النور', location: 'باماكو - المركز', activeUsers: 45, revenue: '125,000 فرنك', status: 'نشط' },
    { name: 'فندق السلام', location: 'باماكو - الشمال', activeUsers: 78, revenue: '285,000 فرنك', status: 'نشط' },
    { name: 'مركز الأعمال', location: 'باماكو - الجنوب', activeUsers: 32, revenue: '95,000 فرنك', status: 'نشط' },
    { name: 'مطعم الواحة', location: 'باماكو - الشرق', activeUsers: 18, revenue: '52,000 فرنك', status: 'نشط' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-6 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <i className="ri-dashboard-line text-3xl"></i>
            </div>
            <div>
              <h1 className="text-2xl font-bold">لوحة التحكم الإدارية</h1>
              <p className="text-white/90">إدارة ومراقبة النظام</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg flex items-center gap-2 transition-all whitespace-nowrap cursor-pointer">
              <i className="ri-notification-3-line text-xl"></i>
              <span className="font-semibold">الإشعارات</span>
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">5</span>
            </button>
            <button className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg flex items-center gap-2 transition-all whitespace-nowrap cursor-pointer">
              <i className="ri-user-line text-xl"></i>
              <span className="font-semibold">المسؤول</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg p-2 mb-8 flex gap-2 overflow-x-auto">
          {[
            { id: 'overview', label: 'نظرة عامة', icon: 'ri-dashboard-line' },
            { id: 'transactions', label: 'المعاملات', icon: 'ri-exchange-line' },
            { id: 'users', label: 'المستخدمين', icon: 'ri-user-line' },
            { id: 'networks', label: 'الشبكات التجارية', icon: 'ri-store-2-line' },
            { id: 'reports', label: 'التقارير', icon: 'ri-file-chart-line' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-600 to-green-600 text-white shadow-lg'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className={`${tab.icon} text-xl`}></i>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-14 h-14 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center`}>
                      <i className={`${stat.icon} text-2xl text-white`}></i>
                    </div>
                    <span className="bg-green-100 text-green-600 text-sm font-bold px-3 py-1 rounded-full">
                      {stat.change}
                    </span>
                  </div>
                  <h3 className="text-gray-600 text-sm mb-2">{stat.label}</h3>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              ))}
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-line-chart-line text-blue-600"></i>
                  الإيرادات الأسبوعية
                </h3>
                <div className="h-64 flex items-end justify-between gap-2">
                  {[65, 85, 45, 95, 75, 55, 90].map((height, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center gap-2">
                      <div
                        className="w-full bg-gradient-to-t from-blue-600 to-green-500 rounded-t-lg transition-all hover:opacity-80 cursor-pointer"
                        style={{ height: `${height}%` }}
                      ></div>
                      <span className="text-xs text-gray-600">
                        {['السبت', 'الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة'][index]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-pie-chart-line text-green-600"></i>
                  توزيع طرق الدفع
                </h3>
                <div className="space-y-4">
                  {[
                    { name: 'Orange Money', percentage: 45, color: 'bg-orange-500' },
                    { name: 'Moov Money', percentage: 30, color: 'bg-blue-500' },
                    { name: 'MTN Mobile Money', percentage: 15, color: 'bg-yellow-500' },
                    { name: 'Wave', percentage: 10, color: 'bg-purple-500' }
                  ].map((method, index) => (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-700 font-medium">{method.name}</span>
                        <span className="text-gray-900 font-bold">{method.percentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className={`${method.color} h-3 rounded-full transition-all`}
                          style={{ width: `${method.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <i className="ri-exchange-line text-blue-600"></i>
                  المعاملات الأخيرة
                </h3>
                <button className="bg-gradient-to-r from-blue-600 to-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer">
                  <i className="ri-download-line ml-2"></i>
                  تصدير التقرير
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">رقم المعاملة</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">المستخدم</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">الباقة</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">المبلغ</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">طريقة الدفع</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">الحالة</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">الوقت</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {recentTransactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm font-mono text-gray-900">{transaction.id}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">{transaction.user}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{transaction.package}</td>
                      <td className="px-6 py-4 text-sm font-bold text-green-600">{transaction.amount} فرنك</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{transaction.method}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          transaction.status === 'مكتمل' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'
                        }`}>
                          {transaction.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{transaction.time}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <i className="ri-user-line text-blue-600"></i>
                المستخدمين النشطين
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">اسم المستخدم</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">الباقة</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">الوقت المتبقي</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">البيانات المستخدمة</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">السرعة</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">الموقع</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">الإجراءات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {activeUsers.map((user, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm font-mono text-gray-900">{user.username}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{user.package}</td>
                      <td className="px-6 py-4 text-sm font-medium text-blue-600">{user.timeLeft}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{user.dataUsed}</td>
                      <td className="px-6 py-4 text-sm font-medium text-green-600">{user.speed}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{user.location}</td>
                      <td className="px-6 py-4">
                        <button className="text-red-600 hover:text-red-700 font-semibold whitespace-nowrap cursor-pointer">
                          <i className="ri-close-circle-line ml-1"></i>
                          قطع الاتصال
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Networks Tab */}
        {activeTab === 'networks' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-900">الشبكات التجارية</h3>
              <button className="bg-gradient-to-r from-blue-600 to-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer">
                <i className="ri-add-line ml-2"></i>
                إضافة شبكة جديدة
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {commercialNetworks.map((network, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-gray-900 mb-1">{network.name}</h4>
                      <p className="text-gray-600 flex items-center gap-2">
                        <i className="ri-map-pin-line"></i>
                        {network.location}
                      </p>
                    </div>
                    <span className="bg-green-100 text-green-600 px-3 py-1 rounded-full text-sm font-bold">
                      {network.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-blue-50 rounded-xl p-4">
                      <p className="text-gray-600 text-sm mb-1">المستخدمين النشطين</p>
                      <p className="text-2xl font-bold text-blue-600">{network.activeUsers}</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4">
                      <p className="text-gray-600 text-sm mb-1">الإيرادات اليوم</p>
                      <p className="text-lg font-bold text-green-600">{network.revenue}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="flex-1 bg-blue-100 text-blue-600 py-2 rounded-lg font-semibold hover:bg-blue-200 transition-all whitespace-nowrap cursor-pointer">
                      <i className="ri-eye-line ml-1"></i>
                      عرض التفاصيل
                    </button>
                    <button className="flex-1 bg-gray-100 text-gray-600 py-2 rounded-lg font-semibold hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer">
                      <i className="ri-settings-3-line ml-1"></i>
                      الإعدادات
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900">التقارير والتحليلات</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: 'تقرير الإيرادات الشهري', icon: 'ri-money-dollar-circle-line', color: 'from-green-500 to-green-600', desc: 'تحليل مفصل للإيرادات' },
                { title: 'تقرير استخدام الباقات', icon: 'ri-pie-chart-line', color: 'from-blue-500 to-blue-600', desc: 'إحصائيات الباقات الأكثر مبيعاً' },
                { title: 'تقرير طرق الدفع', icon: 'ri-wallet-3-line', color: 'from-purple-500 to-purple-600', desc: 'توزيع طرق الدفع المستخدمة' },
                { title: 'تقرير المستخدمين', icon: 'ri-user-line', color: 'from-orange-500 to-orange-600', desc: 'نمو قاعدة المستخدمين' },
                { title: 'تقرير الشبكات التجارية', icon: 'ri-store-2-line', color: 'from-cyan-500 to-cyan-600', desc: 'أداء الشبكات التجارية' },
                { title: 'تقرير استهلاك البيانات', icon: 'ri-database-2-line', color: 'from-pink-500 to-pink-600', desc: 'تحليل استهلاك البيانات' }
              ].map((report, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all cursor-pointer">
                  <div className={`w-16 h-16 bg-gradient-to-br ${report.color} rounded-2xl flex items-center justify-center mb-4`}>
                    <i className={`${report.icon} text-3xl text-white`}></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{report.title}</h4>
                  <p className="text-gray-600 mb-4">{report.desc}</p>
                  <button className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all whitespace-nowrap">
                    <i className="ri-download-line ml-2"></i>
                    تحميل التقرير
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
